<div *ngIf="isPrimary" class="mt-6 border-t border-gray-200 pt-4">
  <!-- Section header -->
  <div class="flex justify-between items-center mb-3">
    <div class="flex items-center gap-2">
      <h2 class="text-xl font-bold">Post-Analysis Metadata</h2>
      <span
        *ngIf="publishedPublications().length"
        class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-300"
      >
        {{ publishedPublications().length }} published
      </span>
    </div>
    <button
      type="button"
      (click)="toggle()"
      class="cursor-pointer hover:text-gray-400 transition-colors"
      [attr.aria-label]="expanded() ? 'Collapse section' : 'Expand section'"
    >
      <fa-icon [icon]="expanded() ? 'compress-arrows-alt' : 'expand-arrows-alt'" class="h-5 w-5"></fa-icon>
    </button>
  </div>

  <div *ngIf="expanded()" class="space-y-4">
    <p class="text-xs text-gray-500">
      Metadata added after promotion based on analysis findings. Published sets are dated and visible in the data
      catalogue and datasets.
    </p>

    <!-- Loading -->
    <div *ngIf="loading()" class="text-sm text-gray-500">Loading…</div>

    <!-- Current set: the active post-analysis metadata (read-only, soft-remove for editors) -->
    <div *ngIf="currentPublication() as pub" class="border border-gray-200 rounded p-3">
      <div class="flex items-center justify-between mb-2">
        <span class="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-300">
            Current
          </span>
          <span>
            Published {{ formatDate(pub.published_at) }}
            <span *ngIf="pub.published_by_name" class="font-normal text-gray-500">by {{ pub.published_by_name }}</span>
          </span>
        </span>
        <span class="text-[11px] text-gray-400">Set #{{ pub.sequence }}</span>
      </div>
      <div *ngIf="!pub.tags.length" class="text-xs text-gray-400 italic">All tags in this set were removed.</div>
      <div class="space-y-1">
        <div
          *ngFor="let tag of pub.tags; trackBy: trackByTagKey"
          class="flex items-start justify-between gap-3 text-sm"
        >
          <div class="flex-1">
            <span class="font-medium">{{ tag.key }}:</span>
            <span class="ml-1">{{ tag.data_type === 'DATE' ? formatDate(asString(tag.value)) : displayValue(tag.value) }}</span>
            <span *ngIf="tag.source_type_name" class="ml-1 text-[11px] text-gray-400">({{ tag.source_type_name }})</span>
            <span
              *ngIf="tag.is_private"
              class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-300"
              title="Lab-only: hidden from the data catalogue"
            >
              Lab only
            </span>
          </div>
          <button
            *ngIf="showEditor() && !tag.is_required"
            type="button"
            (click)="removeTag(pub, tag)"
            class="text-red-400 hover:text-red-600 leading-none text-xs"
            title="Remove this tag (tracked)"
          >
            ✕
          </button>
        </div>
      </div>
    </div>

    <!-- Previous sets: superseded snapshots, collapsed and read-only (audit trail) -->
    <div *ngIf="previousPublications().length" class="text-sm">
      <button
        type="button"
        (click)="toggleHistory()"
        class="text-gray-500 hover:text-gray-700 text-xs font-medium underline-offset-2 hover:underline"
      >
        {{ showHistory() ? 'Hide' : 'View' }} {{ previousPublications().length }} previous
        {{ previousPublications().length === 1 ? 'set' : 'sets' }} (audit)
      </button>

      <div *ngIf="showHistory()" class="space-y-3 mt-2">
        <div
          *ngFor="let pub of previousPublications(); trackBy: trackByPub"
          class="border border-gray-200 rounded p-3 bg-gray-50/60 opacity-80"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-semibold text-gray-600 flex items-center gap-2">
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-200 text-gray-600 border border-gray-300">
                Superseded
              </span>
              <span>
                Published {{ formatDate(pub.published_at) }}
                <span *ngIf="pub.published_by_name" class="font-normal text-gray-500">by {{ pub.published_by_name }}</span>
              </span>
            </span>
            <span class="text-[11px] text-gray-400">Set #{{ pub.sequence }}</span>
          </div>
          <div *ngIf="!pub.tags.length" class="text-xs text-gray-400 italic">All tags in this set were removed.</div>
          <div class="space-y-1">
            <div *ngFor="let tag of pub.tags; trackBy: trackByTagKey" class="text-sm">
              <span class="font-medium">{{ tag.key }}:</span>
              <span class="ml-1">{{ tag.data_type === 'DATE' ? formatDate(asString(tag.value)) : displayValue(tag.value) }}</span>
              <span *ngIf="tag.source_type_name" class="ml-1 text-[11px] text-gray-400">({{ tag.source_type_name }})</span>
              <span
                *ngIf="tag.is_private"
                class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-300"
                title="Lab-only: hidden from the data catalogue"
              >
                Lab only
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      *ngIf="!loading() && !publishedPublications().length && !hasDraft()"
      class="text-sm text-gray-400 italic"
    >
      No post-analysis metadata has been published yet.
    </div>

    <!-- Validation banner -->
    <div
      *ngIf="issues() as iss"
      class="rounded p-3 text-sm"
      [ngClass]="{
        'bg-red-50 border border-red-200': iss.errors.length || iss.missingRequiredKeys.length,
        'bg-amber-50 border border-amber-200': !iss.errors.length && !iss.missingRequiredKeys.length && iss.warnings.length,
      }"
    >
      <p class="font-medium">
        {{
          iss.errors.length || iss.missingRequiredKeys.length
            ? 'This set cannot be published until the errors below are resolved.'
            : 'This set has warnings. Acknowledge them to publish.'
        }}
      </p>
      <ul class="mt-2 list-disc pl-5 text-xs space-y-1">
        <li *ngFor="let key of iss.missingRequiredKeys"><span class="font-semibold">Required:</span> {{ key }} is required.</li>
        <li *ngFor="let issue of iss.errors"><span class="font-semibold">{{ issue.code }}:</span> {{ issue.message }}</li>
        <li *ngFor="let issue of iss.warnings"><span class="font-semibold">Warning ({{ issue.code }}):</span> {{ issue.message }}</li>
      </ul>
    </div>

    <!-- Editor (editors only) -->
    <ng-container *ngIf="showEditor()">
      <!-- Start-draft CTA -->
      <button
        *ngIf="!hasDraft()"
        type="button"
        (click)="openDraft()"
        [disabled]="saving()"
        class="px-4 py-2 rounded-md font-medium text-white bg-[#44b7fd] border border-[#44b7fd] hover:bg-[#2fa3eb] transition-colors disabled:opacity-50"
      >
        {{ saving() ? 'Opening…' : currentPublication() ? 'Update Post-Analysis Metadata' : 'Add Post-Analysis Metadata' }}
      </button>

      <!-- Draft editor -->
      <div *ngIf="draft() as draft" class="border border-blue-200 bg-blue-50/40 rounded p-3 space-y-3">
        <div class="flex items-center justify-between">
          <span class="text-sm font-semibold text-gray-700">New set (draft)</span>
          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-800 border border-blue-300">
            Unpublished
          </span>
        </div>
        <p *ngIf="currentPublication()" class="text-xs text-gray-500 -mt-1">
          Pre-filled from the current set. Edit values, add new ones, or remove what no longer applies, then publish to
          make this the current set (the previous one is kept for audit).
        </p>

        <div *ngIf="!schemaFields().length" class="text-xs text-gray-500 italic">
          No post-analysis fields are configured for this source type yet.
        </div>

        <form [formGroup]="draftForm" class="space-y-3">
          <ng-container *ngFor="let field of schemaFields(); trackBy: trackByField">
            <!-- TEXT -->
            <div *ngIf="field.data_type === 'TEXT'" class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <label [for]="'pa-' + field.id" class="text-sm font-medium col-span-1 pr-2 pt-2">
                {{ field.name }}<span *ngIf="field.is_required" class="text-error text-lg font-bold">*</span>
                <app-requirement-hint [field]="field" [crossFieldRules]="crossFieldRules()" compact />
              </label>
              <div class="col-span-2">
                <input
                  [id]="'pa-' + field.id"
                  [formControlName]="field.name"
                  type="text"
                  class="w-full p-2 border rounded bg-white text-gray-500 focus:ring-blue-500 focus:border-blue-500"
                  [class.border-error]="getControl(field.name)?.invalid"
                  [class.border-gray-400]="!getControl(field.name)?.invalid"
                  [placeholder]="'Enter ' + field.name"
                />
                <p *ngIf="serverErrorFor(field.name) as e" class="text-error text-xs mt-1">{{ e }}</p>
              </div>
            </div>

            <!-- GPS -->
            <div *ngIf="field.data_type === 'GPS'" class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <div class="col-span-1 pr-2">
                <label [for]="'pa-' + field.id" class="text-sm font-medium block">
                  {{ field.name }}<span *ngIf="field.is_required" class="text-error text-lg font-bold">*</span>
                  <app-requirement-hint [field]="field" [crossFieldRules]="crossFieldRules()" compact />
                </label>
                <p class="text-xs text-gray-500 mt-1">Decimal degrees, ≥4 decimal places<br />e.g. 38.9800 N 77.1100 W</p>
              </div>
              <div class="col-span-2">
                <input
                  [id]="'pa-' + field.id"
                  [formControlName]="field.name"
                  type="text"
                  class="w-full p-2 border rounded bg-white text-gray-500 focus:ring-blue-500 focus:border-blue-500"
                  [class.border-error]="getControl(field.name)?.invalid && getControl(field.name)?.touched"
                  placeholder="e.g. 38.9800 N 77.1100 W"
                />
                <p
                  *ngIf="getControl(field.name)?.hasError('pattern') && getControl(field.name)?.touched"
                  class="text-error text-xs mt-1"
                >
                  Invalid GPS format. Use decimal degrees with ≥4 decimal places.
                </p>
                <p *ngIf="serverErrorFor(field.name) as e" class="text-error text-xs mt-1">{{ e }}</p>
              </div>
            </div>

            <!-- NUMBER -->
            <div *ngIf="field.data_type === 'NUMBER'" class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <label [for]="'pa-' + field.id" class="text-sm font-medium col-span-1 pr-2 pt-2">
                {{ field.name }}<span *ngIf="field.is_required" class="text-error text-lg font-bold">*</span>
                <app-requirement-hint [field]="field" [crossFieldRules]="crossFieldRules()" compact />
              </label>
              <div class="col-span-2">
                <input
                  [id]="'pa-' + field.id"
                  [formControlName]="field.name"
                  type="number"
                  class="w-full p-2 border rounded bg-white text-gray-500 focus:ring-blue-500 focus:border-blue-500"
                  [class.border-error]="getControl(field.name)?.invalid"
                  [class.border-gray-400]="!getControl(field.name)?.invalid"
                  [placeholder]="'Enter ' + field.name"
                />
                <p *ngIf="serverErrorFor(field.name) as e" class="text-error text-xs mt-1">{{ e }}</p>
              </div>
            </div>

            <!-- DATE -->
            <div *ngIf="field.data_type === 'DATE'" class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <label [for]="'pa-' + field.id" class="text-sm font-medium col-span-1 pr-2">
                {{ field.name }}<span *ngIf="field.is_required" class="text-error text-lg font-bold">*</span>
                <app-requirement-hint [field]="field" [crossFieldRules]="crossFieldRules()" compact />
              </label>
              <div class="col-span-2">
                <input
                  [id]="'pa-' + field.id"
                  [formControlName]="field.name"
                  type="date"
                  class="w-full p-2 border rounded bg-white text-gray-500 focus:ring-blue-500 focus:border-blue-500"
                  [class.border-error]="getControl(field.name)?.invalid"
                  [class.border-gray-400]="!getControl(field.name)?.invalid"
                />
                <p *ngIf="serverErrorFor(field.name) as e" class="text-error text-xs mt-1">{{ e }}</p>
              </div>
            </div>

            <!-- SELECT (single) -->
            <div
              *ngIf="field.data_type === 'SELECT' && !field.multiple_select"
              class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start"
            >
              <label [for]="'pa-' + field.id" class="text-sm font-medium col-span-1 pr-2 pt-2">
                {{ field.name }}<span *ngIf="field.is_required" class="text-error text-lg font-bold">*</span>
                <app-requirement-hint [field]="field" [crossFieldRules]="crossFieldRules()" compact />
              </label>
              <div class="col-span-2">
                <ng-select
                  [id]="'pa-' + field.id"
                  [items]="field.options"
                  bindLabel="name"
                  bindValue="value"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="Please Select"
                  class="bg-base-100 text-gray-500 border p-2 rounded-md w-full"
                  [formControlName]="field.name"
                >
                  <ng-template ng-footer-tmp>
                    <button
                      type="button"
                      class="block w-full text-left text-[13px] text-info hover:bg-base-200 border-t border-base-300 rounded-b-md px-2 py-2"
                      (mousedown)="$event.preventDefault(); openSuggestOption(field)"
                    >
                      Don't see what you need? Request a new option...
                    </button>
                  </ng-template>
                </ng-select>
                <div *ngIf="pendingValuesForField(field) as pending" class="mt-1 flex flex-wrap items-center gap-1">
                  <span
                    *ngFor="let v of pending"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-900 border border-amber-300"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>{{ v }} (pending)
                  </span>
                  <a
                    *ngIf="pending.length"
                    routerLink="/my-metadata-requests"
                    class="inline-flex items-center gap-1 text-[11px] text-info hover:underline whitespace-nowrap"
                  >
                    My requests
                    <fa-icon [icon]="'arrow-up-right-from-square'" class="text-[9px]"></fa-icon>
                  </a>
                </div>
                <p *ngIf="serverErrorFor(field.name) as e" class="text-error text-xs mt-1">{{ e }}</p>
              </div>
            </div>

            <!-- LIST -->
            <div *ngIf="field.data_type === 'LIST'" class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <div class="col-span-1 pr-2">
                <label [for]="'pa-' + field.id" class="text-sm font-medium block">
                  {{ field.name }}<span *ngIf="field.is_required" class="text-error text-lg font-bold">*</span>
                  <app-requirement-hint [field]="field" [crossFieldRules]="crossFieldRules()" compact />
                </label>
                <p class="text-xs mt-1">Type a value and press Enter or click Add</p>
              </div>
              <div class="col-span-2">
                <div class="flex gap-2">
                  <input
                    #listInput
                    [id]="'pa-' + field.id"
                    type="text"
                    placeholder="Add a value..."
                    class="input input-bordered flex-1 h-10 rounded border-gray-300 text-sm focus:outline-none"
                    (keydown.enter)="$event.preventDefault(); addListValue(field.name, listInput)"
                  />
                  <button
                    type="button"
                    class="h-10 px-3 rounded border border-gray-300 text-sm hover:bg-gray-100"
                    (click)="addListValue(field.name, listInput)"
                  >
                    Add
                  </button>
                </div>
                <div *ngIf="getArray(field.name)?.length" class="mt-2 flex flex-wrap gap-1">
                  <div
                    *ngFor="let control of getArray(field.name)?.controls; let i = index"
                    class="flex items-center gap-1 px-2 py-1 rounded-full text-sm border border-gray-400 bg-white"
                  >
                    <span>{{ control.value }}</span>
                    <button type="button" (click)="removeListValue(field.name, i)" class="text-red-400 hover:text-red-600 leading-none">✕</button>
                  </div>
                </div>
                <p *ngIf="serverErrorFor(field.name) as e" class="text-error text-xs mt-1">{{ e }}</p>
              </div>
            </div>

            <!-- SELECT (multiple) or MULTISELECT -->
            <div
              *ngIf="(field.data_type === 'SELECT' && field.multiple_select) || field.data_type === 'MULTISELECT'"
              class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start"
            >
              <div class="col-span-1 pr-2">
                <label [for]="'pa-' + field.id" class="text-sm font-medium block">
                  {{ field.name }}<span *ngIf="field.is_required" class="text-error text-lg font-bold">*</span>
                  <app-requirement-hint [field]="field" [crossFieldRules]="crossFieldRules()" compact />
                </label>
                <p class="text-xs mt-1">Select one or more</p>
              </div>
              <div class="col-span-2">
                <ng-select
                  [id]="'pa-' + field.id"
                  [items]="field.options"
                  bindLabel="name"
                  bindValue="value"
                  [multiple]="true"
                  [searchable]="true"
                  [clearable]="true"
                  placeholder="Select one or more"
                  class="bg-base-100 text-gray-500 border p-2 rounded-md w-full"
                  [hideSelected]="true"
                  [ngModel]="getArray(field.name)?.value || []"
                  [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="onMultiSelectChange($event, field.name)"
                >
                  <ng-template ng-label-tmp let-item="item"></ng-template>
                  <ng-template ng-footer-tmp>
                    <button
                      type="button"
                      class="block w-full text-left text-[13px] text-info hover:bg-base-200 border-t border-base-300 rounded-b-md px-2 py-2"
                      (mousedown)="$event.preventDefault(); openSuggestOption(field)"
                    >
                      Don't see what you need? Request a new option...
                    </button>
                  </ng-template>
                </ng-select>
                <div *ngIf="pendingValuesForField(field) as pending" class="mt-1 flex flex-wrap items-center gap-1">
                  <span
                    *ngFor="let v of pending"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-900 border border-amber-300"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>{{ v }} (pending)
                  </span>
                </div>
                <div *ngIf="getArray(field.name)?.length" class="mt-2 space-y-1">
                  <div
                    *ngFor="let control of getArray(field.name)?.controls; let i = index"
                    class="flex items-center justify-between px-2 py-1 rounded text-sm border border-gray-400 bg-white"
                  >
                    <span>{{ control.value }}</span>
                    <button type="button" (click)="removeMultiSelectValue(field.name, i)" class="text-red-400 hover:text-red-600">✕</button>
                  </div>
                </div>
                <p *ngIf="serverErrorFor(field.name) as e" class="text-error text-xs mt-1">{{ e }}</p>
              </div>
            </div>
          </ng-container>

          <!-- Custom tags subsection: lab-defined tags reusing the typed widgets -->
          <div class="border-t border-blue-200 pt-3 mt-1 space-y-3">
            <div class="flex items-start justify-between gap-2">
              <div>
                <h3 class="text-sm font-semibold text-gray-700">Custom tags</h3>
                <p class="text-xs text-gray-500">
                  Lab-defined tags. Reuse one of your lab's existing tags, or deliberately create a new one.
                </p>
              </div>
              <button
                type="button"
                (click)="openCreateCustomTag()"
                class="text-xs px-3 py-1.5 rounded border border-[#44b7fd] text-[#2fa3eb] hover:bg-[#44b7fd]/10 whitespace-nowrap"
              >
                + Create new custom tag
              </button>
            </div>

            <ng-select
              [items]="availableCustomTags()"
              bindLabel="name"
              [(ngModel)]="customPickerValue"
              [ngModelOptions]="{ standalone: true }"
              [searchable]="true"
              [clearable]="false"
              placeholder="Add an existing custom tag…"
              class="bg-base-100 text-gray-500 border p-2 rounded-md w-full md:w-2/3"
              (change)="addCustomTag($event)"
            >
              <ng-template ng-option-tmp let-item="item">
                <span>{{ item.name }}</span>
                <span
                  *ngIf="item.is_private"
                  class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-300"
                  >Lab only</span
                >
                <span class="ml-1 text-[10px] text-gray-400">{{ item.data_type }}</span>
              </ng-template>
            </ng-select>

            <p *ngIf="!customFields().length" class="text-xs text-gray-400 italic">No custom tags added to this set.</p>

            <div
              *ngFor="let field of customFields(); trackBy: trackByField"
              class="rounded border border-gray-200 bg-white/70 p-2"
            >
              <div class="flex items-center justify-between mb-1">
                <span class="text-sm font-medium flex items-center gap-2">
                  {{ field.name }}
                  <span
                    *ngIf="isCustomPrivate(field)"
                    class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-300"
                    title="Lab-only: hidden from the data catalogue"
                    >Lab only</span
                  >
                  <span class="text-[10px] text-gray-400">{{ field.data_type }}</span>
                </span>
                <button
                  type="button"
                  (click)="removeCustomTag(field)"
                  class="text-red-400 hover:text-red-600 text-xs leading-none"
                  title="Remove this custom tag from the set"
                >
                  ✕
                </button>
              </div>

              <!-- TEXT -->
              <input
                *ngIf="field.data_type === 'TEXT'"
                [id]="'pac-' + field.id"
                [formControlName]="field.name"
                type="text"
                class="w-full p-2 border rounded bg-white text-gray-500 focus:ring-blue-500 focus:border-blue-500 border-gray-400"
                [placeholder]="'Enter ' + field.name"
              />

              <!-- NUMBER -->
              <input
                *ngIf="field.data_type === 'NUMBER'"
                [id]="'pac-' + field.id"
                [formControlName]="field.name"
                type="number"
                class="w-full p-2 border rounded bg-white text-gray-500 focus:ring-blue-500 focus:border-blue-500 border-gray-400"
                [placeholder]="'Enter ' + field.name"
              />

              <!-- DATE -->
              <input
                *ngIf="field.data_type === 'DATE'"
                [id]="'pac-' + field.id"
                [formControlName]="field.name"
                type="date"
                class="w-full p-2 border rounded bg-white text-gray-500 focus:ring-blue-500 focus:border-blue-500 border-gray-400"
              />

              <!-- GPS -->
              <ng-container *ngIf="field.data_type === 'GPS'">
                <input
                  [id]="'pac-' + field.id"
                  [formControlName]="field.name"
                  type="text"
                  class="w-full p-2 border rounded bg-white text-gray-500 focus:ring-blue-500 focus:border-blue-500"
                  [class.border-error]="getControl(field.name)?.invalid && getControl(field.name)?.touched"
                  [class.border-gray-400]="!(getControl(field.name)?.invalid && getControl(field.name)?.touched)"
                  placeholder="e.g. 38.9800 N 77.1100 W"
                />
                <p
                  *ngIf="getControl(field.name)?.hasError('pattern') && getControl(field.name)?.touched"
                  class="text-error text-xs mt-1"
                >
                  Invalid GPS format. Use decimal degrees with ≥4 decimal places.
                </p>
              </ng-container>

              <!-- SELECT (single) -->
              <ng-select
                *ngIf="field.data_type === 'SELECT' && !field.multiple_select"
                [id]="'pac-' + field.id"
                [items]="field.options"
                bindLabel="name"
                bindValue="value"
                [clearable]="true"
                [searchable]="true"
                placeholder="Please Select"
                class="bg-base-100 text-gray-500 border p-2 rounded-md w-full"
                [formControlName]="field.name"
              ></ng-select>

              <!-- SELECT (multiple) -->
              <div *ngIf="field.data_type === 'SELECT' && field.multiple_select">
                <ng-select
                  [id]="'pac-' + field.id"
                  [items]="field.options"
                  bindLabel="name"
                  bindValue="value"
                  [multiple]="true"
                  [searchable]="true"
                  [clearable]="true"
                  [hideSelected]="true"
                  placeholder="Select one or more"
                  class="bg-base-100 text-gray-500 border p-2 rounded-md w-full"
                  [ngModel]="getArray(field.name)?.value || []"
                  [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="onMultiSelectChange($event, field.name)"
                >
                  <ng-template ng-label-tmp let-item="item"></ng-template>
                </ng-select>
                <div *ngIf="getArray(field.name)?.length" class="mt-2 space-y-1">
                  <div
                    *ngFor="let control of getArray(field.name)?.controls; let i = index"
                    class="flex items-center justify-between px-2 py-1 rounded text-sm border border-gray-400 bg-white"
                  >
                    <span>{{ control.value }}</span>
                    <button type="button" (click)="removeMultiSelectValue(field.name, i)" class="text-red-400 hover:text-red-600">✕</button>
                  </div>
                </div>
              </div>

              <!-- LIST -->
              <div *ngIf="field.data_type === 'LIST'">
                <div class="flex gap-2">
                  <input
                    #customListInput
                    [id]="'pac-' + field.id"
                    type="text"
                    placeholder="Add a value..."
                    class="input input-bordered flex-1 h-10 rounded border-gray-300 text-sm focus:outline-none"
                    (keydown.enter)="$event.preventDefault(); addListValue(field.name, customListInput)"
                  />
                  <button
                    type="button"
                    class="h-10 px-3 rounded border border-gray-300 text-sm hover:bg-gray-100"
                    (click)="addListValue(field.name, customListInput)"
                  >
                    Add
                  </button>
                </div>
                <div *ngIf="getArray(field.name)?.length" class="mt-2 flex flex-wrap gap-1">
                  <div
                    *ngFor="let control of getArray(field.name)?.controls; let i = index"
                    class="flex items-center gap-1 px-2 py-1 rounded-full text-sm border border-gray-400 bg-white"
                  >
                    <span>{{ control.value }}</span>
                    <button type="button" (click)="removeListValue(field.name, i)" class="text-red-400 hover:text-red-600 leading-none">✕</button>
                  </div>
                </div>
              </div>

              <p *ngIf="serverErrorFor(field.name) as e" class="text-error text-xs mt-1">{{ e }}</p>
            </div>
          </div>
        </form>

        <!-- Draft actions -->
        <div class="flex justify-end gap-3 pt-2">
          <button
            type="button"
            (click)="saveDraft()"
            [disabled]="saving() || publishing()"
            class="px-5 py-2 border border-gray-400 rounded bg-transparent hover:bg-gray-200 transition disabled:opacity-50"
          >
            {{ saving() ? 'Saving…' : 'Save draft' }}
          </button>
          <button
            *ngIf="!hasOnlyWarnings()"
            type="button"
            (click)="publish()"
            [disabled]="saving() || publishing() || draftForm.invalid"
            class="px-4 py-2 rounded-md font-medium text-white bg-[#44b7fd] border border-[#44b7fd] hover:bg-[#2fa3eb] transition disabled:opacity-50"
          >
            {{ publishing() ? 'Publishing…' : 'Publish' }}
          </button>
          <button
            *ngIf="hasOnlyWarnings()"
            type="button"
            (click)="acknowledgeAndPublish()"
            [disabled]="publishing()"
            class="px-4 py-2 rounded-md font-medium text-white bg-amber-500 border border-amber-500 hover:bg-amber-600 transition disabled:opacity-50"
          >
            {{ publishing() ? 'Publishing…' : 'Acknowledge & Publish' }}
          </button>
        </div>
      </div>
    </ng-container>
  </div>
</div>

<!-- Suggest a new option for a post-analysis SELECT/MULTISELECT field -->
<app-suggest-option-modal
  *ngIf="suggestTemplateId != null"
  [fileIds]="[fileId]"
  [templateId]="suggestTemplateId"
  [keyName]="suggestKeyName"
  (submitted_)="onSuggestionSubmitted()"
></app-suggest-option-modal>

<!-- Create a new lab custom post-analysis tag -->
<app-create-custom-tag-modal
  *ngIf="showEditor()"
  [fileId]="fileId"
  (created)="onCustomTagCreated($event)"
></app-create-custom-tag-modal>
